'use client';

import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useContext, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ChatBubbles, UHLogo } from 'assets/icons';
import { BookCallTFComponentDiv } from 'components/home/BottomBar';
import CustomLink from 'components/shared/CustomLink';
import { RegionSelector } from 'components/shared/RegionSelector';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import {
  GlobalUIContext,
  type HeaderCtaOverride,
} from 'contexts/GlobalUIContext';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { analytics } from 'lib/analytics/analytics';
import { isMobileDevice } from 'utils/isMobile';
import {
  freezeBackdropForMenu,
  unfreezeBackdropForMenu,
} from 'utils/freezeBackdropForMenu';
import { usesPerformanceLabNavbar } from 'components/performance-lab/performanceLabNavRoutes';
import { useOpenCallbackModal } from 'components/performance-lab/hooks';
import { isNoHeaderOrFooterPage, isOrderPage } from '../constants';
import { useModal } from '../Modal';
import { UhAppModal } from '../UHAppDownloadModal';
import { CartIcon } from './Cart';
import { MarketingBanner, useMarketingBannerOffset } from './MarketingBanner';
import { MegaMenu } from './MegaMenu';
import { MobileDrawer } from './MobileDrawer';
import { HamburgerMorphIcon } from './HamburgerMorphIcon';
import { HeaderLogo } from './HeaderLogo';
import { headerLinkAttribution } from './headerAnalytics';
import {
  DESKTOP_BAR_HEIGHT_PX,
  HEADER_BAR_HEIGHT_PX,
  PILL_TOP_MARGIN_PX,
} from './marketingBannerVisibility';
import {
  MORE_MENU_KEY,
  MORE_MENU_SECTIONS,
  NAV_ITEMS,
  NavItem,
} from './navConfig';
import { HeaderTheme, resolveHeaderTheme } from './theme';
import themeStyles from './theme.module.css';
import styles from './Header.module.css';

const MOBILE_BREAKPOINT = 900;

/* power3.out, matched by cubic-bezier(0.22, 1, 0.36, 1) in Header.module.css.
 * This is the slowest the morph runs. A fast scroll shortens it, down to
 * PILL_GSAP_DURATION * MIN_MOTION_SCALE. */
const PILL_GSAP_DURATION = 0.5;
const PILL_GSAP_EASE = 'power3.out';
/** Below this wheel speed, the morph keeps its full duration. */
const SLOW_SCROLL_PX_PER_S = 900;
/** A hard flick. The morph is at its shortest here. */
const FAST_SCROLL_PX_PER_S = 2800;
/**
 * Shortest shared duration, as a fraction of the full 0.5s.
 * Halfway between the snap that felt too fast and the full timing that lagged.
 */
const MIN_MOTION_SCALE = 0.55;
/** Window the scroll speed is averaged over, so one Lenis frame doesn't lie. */
const SCROLL_SPEED_WINDOW_MS = 180;
/** ScrollY where the bar compresses to the floating pill. */
const SCROLL_COMPRESS_Y = 48;
/** ScrollY where it expands again — during upward scroll, not only at y≈0. */
const SCROLL_EXPAND_Y = 28;
/** Only blocks compress-after-expand (Lenis top settle), never expand. */
const COMPRESS_LOCK_AFTER_EXPAND_MS = 180;

/** 1 at a slow scroll, MIN_MOTION_SCALE at a flick. */
const motionScaleForSpeed = (pxPerSec: number) => {
  const t =
    (pxPerSec - SLOW_SCROLL_PX_PER_S) /
    (FAST_SCROLL_PX_PER_S - SLOW_SCROLL_PX_PER_S);
  const clamped = Math.min(1, Math.max(0, t));
  return 1 - clamped * (1 - MIN_MOTION_SCALE);
};

/**
 * Routes that opt into hide-on-scroll-down. Derived from the route rather than
 * read from `GlobalUI.headerCollaspable`, which the provider resets on every
 * navigation — route derivation makes collapse deterministic. Science is
 * deliberately absent: those pages use the homepage pill.
 */
const HEADER_COLLAPSABLE_ROUTES = new Set<string>([
  '/home/buy',
  '/blood-vision/buy',
  '/photon',
  '/photon/buy',
  '/rare/buy',
  '/performance-lab/buy',
]);

/**
 * Full-width header, no compact pill. Mid-page they take the scrolled-pill
 * frost so labels stay readable; geometry stays full-size. Hide-on-scroll is a
 * separate gate — the ring buy routes are in this set only.
 */
const HEADER_NO_PILL_MORPH_ROUTES = new Set<string>([
  '/home/buy',
  '/photon',
  '/photon/buy',
  '/rare/buy',
  '/blood-vision/buy',
  '/blood-vision/buy/checkout',
  '/ring/buy',
  '/ring-pro/buy',
]);

/**
 * Light-themed routes whose hero is dark: they need an opaque white bar instead
 * of the default transparent top, or the #111 labels vanish. `/` is included —
 * its hero is light beige, so a transparent top would drop the white CTA and
 * the 16px logo into the plate. Purchase CTAs go blue on this chrome so a white
 * pill does not disappear against it.
 *
 * `/blood-vision/buy/checkout` is a flat #f3f3f3 form (user details + slot
 * booking) with almost no scroll — a transparent top leaves the bar floating
 * over the page with no surface.
 */
const OPAQUE_LIGHT_BAR_ROUTES = new Set<string>([
  '/',
  '/home',
  '/photon',
  '/investorRelations',
  '/timeout',
  '/troubleshoot',
  '/join_group',
  '/pulsomics',
  '/how-it-works',
  '/blood-vision/buy/checkout',
]);

const getHeaderBuyLink = (
  pathname: string,
  country?: string,
): { pathname: string; labelKey: string } => {
  const isUS = country === 'US' || country === 'PR';

  if (pathname.startsWith('/home'))
    return { pathname: '/home/buy', labelKey: 'header.buyNow' };
  if (pathname.startsWith('/diesel-ultrahuman-ring'))
    return {
      pathname: '/diesel-ultrahuman-ring/buy',
      labelKey: 'header.buyNow',
    };
  if (pathname.startsWith('/photon'))
    return { pathname: '/photon/buy', labelKey: 'header.buyNow' };
  if (pathname.startsWith('/ring-pro'))
    return {
      pathname: '/ring-pro/buy',
      labelKey: 'header.buyRingPro',
    };
  if (isUS) return { pathname: '/ring-pro/buy', labelKey: 'header.buyRingPro' };
  return { pathname: '/ring/buy', labelKey: 'header.buyRingAir' };
};

type HeaderCTAResult = {
  type: 'link' | 'button' | 'none';
  text: string;
  href?: string;
  onClick?: () => void;
  icon?: 'chat';
  ariaLabel?: string;
};

const BLOCKED_PAGES = [
  '/shop/',
  '/sizing-kit',
  '/x/',
  '/ogdb',
  '/rare',
  '/heroes',
];

const getHeaderCTA = ({
  pathname,
  country,
  t,
  headerBuyLink,
  showBookCallModal,
  handleUHAppClick,
  openCallbackModal,
  override,
}: {
  pathname: string;
  country: string;
  t: (key: string) => string;
  headerBuyLink: { pathname: string; labelKey: string };
  showBookCallModal: () => void;
  handleUHAppClick: () => void;
  openCallbackModal?: () => void;
  /** Page-declared CTA (useGlobalUI({ headerCta })); beats every route rule. */
  override?: HeaderCtaOverride;
}): {
  mobile: HeaderCTAResult;
  desktop: HeaderCTAResult;
  bloodVision?: HeaderCTAResult;
} => {
  const none: HeaderCTAResult = { type: 'none', text: '' };
  const both = (cta: HeaderCTAResult) => ({ mobile: cta, desktop: cta });

  const talkToExpert: HeaderCTAResult = {
    type: 'button',
    text: t('header.talkToExpert'),
    onClick: showBookCallModal,
    icon: 'chat',
  };

  if (override === 'talkToExpert') return both(talkToExpert);

  // Blood Vision India mobile special case (additional CTA)
  const bloodVision: HeaderCTAResult | undefined =
    pathname === '/blood-vision' && country === 'IN'
      ? {
          type: 'link',
          text: t('header.bookTest'),
          href: '/ring/buy',
          onClick: handleUHAppClick,
        }
      : undefined;

  // 0. Performance Lab. Only /performance-lab/buy and /performance-lab/leaderboard
  // reach here — every other PL route renders its own navbar and this header
  // returns null before HeaderShell mounts. Kept ahead of the /buy branch below
  // so the buy page keeps its callback CTA instead of Talk to an expert.
  if (pathname.startsWith('/performance-lab')) {
    if (pathname === '/performance-lab/buy') {
      const bookCallback: HeaderCTAResult = {
        type: 'button',
        text: t('header.bookCallback'),
        onClick: openCallbackModal ?? (() => {}),
        ariaLabel: t('header.bookCallback'),
      };
      return { ...both(bookCallback), bloodVision };
    }
    return { ...both(none), bloodVision };
  }

  // 1. Buy/Pricing pages → Talk to an expert (desktop only)
  if (
    pathname.endsWith('/buy') ||
    pathname.includes('/buy/') ||
    pathname.startsWith('/pricing')
  ) {
    return { mobile: none, desktop: talkToExpert, bloodVision };
  }

  // 2. Homepage → Ring PRO buy. India names the product so the CTA is
  // not read as Ring AIR (IN first fold is the 2-hour Air card).
  if (pathname === '/') {
    const homeBuy: HeaderCTAResult = {
      type: 'link',
      text: country === 'IN' ? t('header.buyRingPro') : t('header.buyNow'),
      href: '/ring-pro/buy',
    };
    return { ...both(homeBuy), bloodVision };
  }

  // 3. CGM marketing page → Buy now (the CGM buy page, not Ring)
  if (pathname.startsWith('/m1')) {
    const buyNow: HeaderCTAResult = {
      type: 'link',
      text: t('header.buyNow'),
      href: '/pricing',
    };
    return { ...both(buyNow), bloodVision };
  }

  // 4. Blood Vision pages → Book test
  if (pathname.startsWith('/blood-vision')) {
    const bookTest: HeaderCTAResult = {
      type: 'link',
      text: t('header.bookTest'),
      href: '/blood-vision/buy',
    };
    return { ...both(bookTest), bloodVision };
  }

  // 5. Ring product pages → Buy now
  if (
    pathname.startsWith('/ring') ||
    pathname.startsWith('/diesel-ultrahuman-ring') ||
    pathname.startsWith('/home') ||
    pathname.startsWith('/photon')
  ) {
    const buyNow: HeaderCTAResult = {
      type: 'link',
      text: t(headerBuyLink.labelKey),
      href: headerBuyLink.pathname,
    };
    return { ...both(buyNow), bloodVision };
  }

  // 6. Other blocked pages → Talk to an expert (desktop only)
  if (BLOCKED_PAGES.some((p) => pathname === p || pathname.startsWith(p))) {
    return { mobile: none, desktop: talkToExpert, bloodVision };
  }

  // 7. Default → Buy Ring PRO in every region
  const defaultBuy: HeaderCTAResult = {
    type: 'link',
    text: t('header.buyNow'),
    href: '/ring-pro/buy',
  };
  return { ...both(defaultBuy), bloodVision };
};

export const Header = () => {
  const GlobalUI = useContext(GlobalUIContext);
  const routePath = useHeaderRoutePath();

  if (
    GlobalUI.liteUI ||
    isNoHeaderOrFooterPage(routePath) ||
    isOrderPage(routePath) ||
    usesPerformanceLabNavbar(routePath)
  ) {
    return null;
  }

  return <HeaderShell />;
};

/** Internal path with the trailing slash trimmed, as the route tables expect. */
const useHeaderRoutePath = () => {
  const internalPathname = useInternalPathname();
  return internalPathname.length > 1
    ? internalPathname.replace(/\/$/, '')
    : internalPathname;
};

const HeaderShell = () => {
  const t = useTranslations('common');
  const routePath = useHeaderRoutePath();
  const searchParams = useSearchParams();
  const { width } = useWindowSize();
  const isMobile = (width ?? 0) < MOBILE_BREAKPOINT;
  const { region: country, regionSlug } = useRegionLocale();
  const ModalClient = useModal();
  const GlobalUI = useContext(GlobalUIContext);
  const setHeaderHeight = GlobalUI.setHeaderHeight;
  const bannerOffsetPx = useMarketingBannerOffset();

  // A page may flip the theme at runtime (e.g. /science: dark over the full-bleed
  // hero, light once it scrolls out). null → the route-derived theme, unchanged.
  const theme: HeaderTheme =
    GlobalUI.headerThemeOverride ??
    resolveHeaderTheme(routePath, searchParams?.get('headerTheme'));

  // Opaque white desktop-unscrolled chrome for light-theme routes over a dark
  // hero (and for the homepage's light-beige plate).
  const isOpaqueLightBar = OPAQUE_LIGHT_BAR_ROUTES.has(routePath);

  // Top-level tab region-gating, filtered ONCE here so the desktop nav,
  // mega-menu and mobile drawer read the same list and cannot disagree.
  // `country` is SSR-resolved via RegionLocaleContext, so no hydration drift.
  const visibleNavItems: NavItem[] = NAV_ITEMS.filter(
    (item) => !item.regionsOnly || item.regionsOnly.includes(country),
  );

  const [openKey, setOpenKey] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Scroll-adaptive compact frost: false at the top (see-through over the hero),
  // true once scrolled over content. SSR-safe (scrollY is 0 pre-hydration).
  const [scrolled, setScrolled] = useState(false);
  // No-pill routes: same Y hysteresis as `scrolled`, but only the frost fill —
  // never geometry, banner collapse or headerHeight.
  const [barFrosted, setBarFrosted] = useState(false);

  const showBookCallModal = () => {
    analytics.track(`header talk to expert - CLICK`);
    let url = 'website';
    if (typeof window !== 'undefined') {
      url = window.location.hostname + window.location.pathname;
    }
    ModalClient.set(
      <BookCallTFComponentDiv
        trackingParams={{ utm_source: url, utm_medium: 'header-cta' }}
      />,
    );
    ModalClient.setCloseButtonTheme('light');
    ModalClient.show();
  };

  const handleUHAppClick = () => {
    if (isMobileDevice()) {
      window.open('https://ultrahuman.onelink.me/QqSM/0ncoj3us', '_blank');
    } else {
      ModalClient.set(
        <UhAppModal
          text={t('common.header.installUltrahumanAppBloodVision')}
          link="https://ultrahuman.onelink.me/QqSM/0ncoj3us"
          image="web_v2/qr-codes/blood-vision-onelink.png"
        />,
      );
      ModalClient.show();
    }
  };

  const openCallbackModal = useOpenCallbackModal();

  const headerBuyLink = getHeaderBuyLink(routePath, country);

  const headerCTA = getHeaderCTA({
    pathname: routePath,
    country,
    t,
    headerBuyLink,
    showBookCallModal,
    handleUHAppClick,
    openCallbackModal,
    override: GlobalUI.headerCta,
  });

  const renderCta =(cta: HeaderCTAResult, variant?: 'blue' | 'white') => {
    if (cta.type === 'none') return null;
    // Talk to an expert is always white. Purchase CTAs are white when parked and
    // brand blue when scrolled, or blue on the opaque bar so they stay visible.
    const tone = variant ?? (cta.type === 'button' ? 'white' : 'blue');
    const shared = {
      className: styles.slimCta,
      'data-variant': tone,
      'data-scrolled': String(scrolled || barFrosted),
      'data-compact': String(scrolled || barFrosted || isOpaqueLightBar),
      'aria-label': cta.ariaLabel,
      'data-buttontype': 'header',
    };

    if (cta.type === 'button') {
      return (
        <button {...shared} type="button" onClick={cta.onClick}>
          {cta.text}
          {cta.icon === 'chat' ? <ChatBubbles /> : null}
        </button>
      );
    }
    return (
      <CustomLink
        {...shared}
        href={{ pathname: cta.href ?? '#' }}
        onClick={
          cta.onClick
            ? (e) => {
                e.preventDefault();
                cta.onClick?.();
              }
            : undefined
        }
      >
        {cta.text}
      </CustomLink>
    );
  };

  const triggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const registerTriggerRef = (key: string, el: HTMLButtonElement | null) => {
    if (el) triggerRefs.current.set(key, el);
    else triggerRefs.current.delete(key);
  };

  // When a panel was just HOVER-opened the mouse is by definition on the
  // trigger, so the first click must not toggle it closed (click follows hover).
  // The first click after a hover-open confirms; the next deliberate click — or
  // any keyboard toggle, which never sets the flag — closes as normal.
  const hoverOpenedKeyRef = useRef<string | null>(null);

  const handleToggleMenu = (key: string) => {
    // Read + clear OUTSIDE the updater: updaters must be pure (StrictMode
    // double-invokes them in dev, which would clear the flag on the first pass
    // and close on the second).
    const wasHoverOpened = hoverOpenedKeyRef.current === key;
    hoverOpenedKeyRef.current = null;
    setOpenKey((prev) => {
      if (prev === key) return wasHoverOpened ? prev : null;
      return key;
    });
  };

  const openPanelAndFocusFirst = (key: string) => {
    setOpenKey(key);
    requestAnimationFrame(() => {
      document
        .getElementById(`mega-panel-${key}`)
        ?.querySelector<HTMLElement>('a[href], button:not([disabled])')
        ?.focus();
    });
  };

  const hamburgerRef = useRef<HTMLButtonElement>(null);

  const closeTimerRef = useRef<number | null>(null);
  const clearCloseTimer = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      // The "More" panel is click-opened, so it must NOT auto-close on
      // mouse-leave the way the hover-opened tabs do — only Escape, re-click,
      // click-away or opening a tab dismisses it.
      setOpenKey((prev) => (prev === MORE_MENU_KEY ? prev : null));
      closeTimerRef.current = null;
    }, 180);
  };
  useEffect(() => () => clearCloseTimer(), []);

  // Click-away closes the "More" panel — the one menu that persists through
  // mouse-leave. Clicks on the toggle or inside the panel are ignored.
  useEffect(() => {
    if (openKey !== MORE_MENU_KEY) return;
    const onPointerDown = (e: globalThis.MouseEvent) => {
      const target = e.target as Node;
      if (triggerRefs.current.get(MORE_MENU_KEY)?.contains(target)) return;
      if (
        document.getElementById(`mega-panel-${MORE_MENU_KEY}`)?.contains(target)
      )
        return;
      setOpenKey(null);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [openKey]);

  useEffect(() => {
    if (openKey === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenKey(null);
        triggerRefs.current.get(openKey)?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openKey]);

  // App Router has no routeChangeStart event; the pathname changing IS the
  // navigation, so close every overlay when it does.
  useEffect(() => {
    setOpenKey(null);
    setDrawerOpen(false);
  }, [routePath]);

  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
    if (isMobile) setOpenKey(null);
  }, [isMobile]);

  // Keep the scrim / drawer blur sampling a still frame: pause playing videos
  // and notify WebGL + carousel listeners while any header overlay is open. The
  // boolean dep avoids unfreeze/refreeze when switching tabs.
  const backdropFrozen = openKey !== null || drawerOpen;
  useEffect(() => {
    if (!backdropFrozen) return;
    freezeBackdropForMenu();
    return () => unfreezeBackdropForMenu();
  }, [backdropFrozen]);

  const isCollapsable = HEADER_COLLAPSABLE_ROUTES.has(routePath);
  const skipPillMorph = HEADER_NO_PILL_MORPH_ROUTES.has(routePath);

  // Publishes the sticky-nav offset every consumer reads from
  // `GlobalUI.headerHeight`: mobile 44 + banner, desktop 56 + banner at rest,
  // and 12 + 56 once the banner collapses behind the floating pill.
  useEffect(() => {
    const publish = () => {
      const desktop = window.innerWidth >= MOBILE_BREAKPOINT;
      if (!desktop) {
        setHeaderHeight(HEADER_BAR_HEIGHT_PX + bannerOffsetPx);
        return;
      }
      setHeaderHeight(
        scrolled
          ? PILL_TOP_MARGIN_PX + DESKTOP_BAR_HEIGHT_PX
          : DESKTOP_BAR_HEIGHT_PX + bannerOffsetPx,
      );
    };
    publish();
    window.addEventListener('resize', publish, { passive: true });
    return () => window.removeEventListener('resize', publish);
  }, [scrolled, bannerOffsetPx, setHeaderHeight]);

  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const scrolledRef = useRef(false);
  const barFrostedRef = useRef(false);
  const headerVisibleRef = useRef<boolean>(!!GlobalUI.headerVisible);
  const collapsableRef = useRef(isCollapsable);
  const skipPillMorphRef = useRef(skipPillMorph);
  const setHeaderVisibleRef = useRef(GlobalUI.setHeaderVisible);

  useEffect(() => {
    headerVisibleRef.current = !!GlobalUI.headerVisible;
  }, [GlobalUI.headerVisible]);

  useEffect(() => {
    collapsableRef.current = isCollapsable;
  }, [isCollapsable]);

  useEffect(() => {
    skipPillMorphRef.current = skipPillMorph;
  }, [skipPillMorph]);

  useEffect(() => {
    setHeaderVisibleRef.current = GlobalUI.setHeaderVisible;
  }, [GlobalUI.setHeaderVisible]);

  // After expand, briefly ignore re-compress so a Lenis settle near the top
  // cannot bounce compact→full→compact. Expand itself is NEVER locked.
  const compressLockUntilRef = useRef(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const motionScaleRef = useRef(1);
  const scrollSamplesRef = useRef<{ y: number; t: number }[]>([]);
  const wheelSamplesRef = useRef<{ dy: number; t: number }[]>([]);
  const barAndPanelRef = useRef<HTMLDivElement>(null);
  const barInnerRef = useRef<HTMLDivElement>(null);
  const pillTweenReadyRef = useRef(false);

  const scrollSpeed = () => {
    const samples = scrollSamplesRef.current;
    if (samples.length < 2) return 0;
    const first = samples[0];
    const last = samples[samples.length - 1];
    let dt = last.t - first.t;
    if (dt <= 0) return 0;
    // A single jump after an idle gap is one event. Don't average it down
    // across the pause; a large step is a fast scroll.
    if (dt > SCROLL_SPEED_WINDOW_MS) dt = Math.min(dt, 32);
    let peak = (Math.abs(last.y - first.y) / dt) * 1000;
    for (let i = 1; i < samples.length; i++) {
      const stepDt = samples[i].t - samples[i - 1].t;
      if (stepDt <= 0) continue;
      const step = (Math.abs(samples[i].y - samples[i - 1].y) / stepDt) * 1000;
      if (step > peak) peak = step;
    }
    return peak;
  };

  const wheelSpeed = () => {
    const now = performance.now();
    const samples = wheelSamplesRef.current.filter(
      (sample) => now - sample.t <= SCROLL_SPEED_WINDOW_MS,
    );
    if (samples.length < 2) return 0;
    const dt = samples[samples.length - 1].t - samples[0].t;
    if (dt <= 0) return 0;
    const dy = samples.reduce((sum, sample) => sum + sample.dy, 0);
    return (dy / dt) * 1000;
  };

  const applyMotionScale = () => {
    // Lenis eases window scroll, so a flick looks slow in scrollY. The wheel
    // deltas are the gesture itself. The logo and the pill share this scale.
    const scale = motionScaleForSpeed(Math.max(scrollSpeed(), wheelSpeed()));
    motionScaleRef.current = scale;
    wrapperRef.current?.style.setProperty('--hdr-motion', String(scale));
  };

  const noteScrollSample = (currentY: number) => {
    const now = performance.now();
    const samples = scrollSamplesRef.current;
    samples.push({ y: currentY, t: now });
    const cutoff = now - SCROLL_SPEED_WINDOW_MS;
    while (samples.length > 2 && samples[0].t < cutoff) samples.shift();
  };

  const syncBarFrostFromY = (currentY: number) => {
    if (currentY < SCROLL_EXPAND_Y && barFrostedRef.current) {
      applyMotionScale();
      barFrostedRef.current = false;
      setBarFrosted(false);
      return;
    }
    if (currentY > SCROLL_COMPRESS_Y && !barFrostedRef.current) {
      applyMotionScale();
      barFrostedRef.current = true;
      setBarFrosted(true);
    }
  };

  const syncScrolledFromY = (currentY: number) => {
    // No-pill buy layouts never enter the compact pill; mid-page frost still
    // tracks the same thresholds so labels stay readable when the full bar
    // returns over product chrome.
    if (skipPillMorphRef.current) {
      if (scrolledRef.current) {
        scrolledRef.current = false;
        setScrolled(false);
      }
      syncBarFrostFromY(currentY);
      return;
    }

    if (barFrostedRef.current) {
      barFrostedRef.current = false;
      setBarFrosted(false);
    }

    if (currentY < SCROLL_EXPAND_Y && scrolledRef.current) {
      applyMotionScale();
      scrolledRef.current = false;
      setScrolled(false);
      compressLockUntilRef.current =
        performance.now() + COMPRESS_LOCK_AFTER_EXPAND_MS;
      return;
    }

    if (
      currentY > SCROLL_COMPRESS_Y &&
      !scrolledRef.current &&
      performance.now() >= compressLockUntilRef.current
    ) {
      applyMotionScale();
      scrolledRef.current = true;
      setScrolled(true);
    }
  };

  // Reset scroll tracking on navigation.
  useEffect(() => {
    lastScrollY.current = window.scrollY;
    scrollSamplesRef.current = [];
    wheelSamplesRef.current = [];
    compressLockUntilRef.current = 0;
    syncScrolledFromY(window.scrollY);
  }, [routePath]);

  useEffect(() => {
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      requestAnimationFrame(() => {
        ticking.current = false;
        const currentY = window.scrollY;
        noteScrollSample(currentY);

        syncScrolledFromY(currentY);

        // Hide-on-scroll collapse is route-gated.
        if (!collapsableRef.current) return;

        const lastY = lastScrollY.current;
        lastScrollY.current = currentY;

        if (currentY < 48) {
          setHeaderVisibleRef.current(true);
          return;
        }

        const scrollingUp = currentY < lastY;
        if (scrollingUp !== headerVisibleRef.current) {
          setHeaderVisibleRef.current(scrollingUp);
        }
      });
    };
    syncScrolledFromY(window.scrollY);
    const rafId = requestAnimationFrame(() =>
      syncScrolledFromY(window.scrollY),
    );
    const onPageShow = () => syncScrolledFromY(window.scrollY);

    const onWheel = (event: WheelEvent) => {
      const now = performance.now();
      const unit =
        event.deltaMode === 1
          ? 16
          : event.deltaMode === 2
            ? window.innerHeight
            : 1;
      const samples = wheelSamplesRef.current;
      samples.push({ dy: Math.abs(event.deltaY) * unit, t: now });
      const cutoff = now - SCROLL_SPEED_WINDOW_MS;
      while (samples.length > 2 && samples[0].t < cutoff) samples.shift();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('wheel', onWheel, { passive: true, capture: true });
    window.addEventListener('pageshow', onPageShow);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('wheel', onWheel, { capture: true });
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  // Pill geometry via GSAP — discrete on `scrolled`, not scroll-scrubbed.
  // `overwrite: 'auto'` lets a reversal mid-morph start from the current values,
  // which is smoother than toggling CSS transitions.
  useEffect(() => {
    const bar = barAndPanelRef.current;
    const inner = barInnerRef.current;
    if (!bar) return;

    const clearDesktopProps = () => {
      gsap.killTweensOf(bar);
      gsap.set(bar, {
        clearProps:
          'maxWidth,marginTop,marginLeft,marginRight,marginBottom,borderRadius',
      });
      if (inner) {
        gsap.killTweensOf(inner);
        gsap.set(inner, { clearProps: 'paddingLeft,paddingRight' });
      }
    };

    const tweenPill = (compact: boolean, immediate: boolean) => {
      if (window.innerWidth < MOBILE_BREAKPOINT) {
        clearDesktopProps();
        return;
      }
      // No-pill buy layouts stay full-width even if `scrolled` flickered true
      // for a frame on route change.
      const goCompact = skipPillMorphRef.current ? false : compact;

      const vw = window.innerWidth;
      const narrow = vw <= 1080;
      const pillW = narrow ? vw - 16 : Math.min(1280, vw * 0.9);
      const side = Math.max(0, (vw - pillW) / 2);
      const pad = goCompact ? 24 : vw <= 1023 ? 24 : vw <= 1439 ? 40 : 48;
      const padRight = goCompact ? 12 : pad;
      const reduceMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches;
      const duration =
        immediate || reduceMotion
          ? 0
          : PILL_GSAP_DURATION * motionScaleRef.current;

      gsap.to(bar, {
        maxWidth: goCompact ? pillW : vw,
        marginTop: goCompact ? PILL_TOP_MARGIN_PX : 0,
        marginLeft: goCompact ? (narrow ? 8 : side) : 0,
        marginRight: goCompact ? (narrow ? 8 : side) : 0,
        marginBottom: 0,
        borderRadius: goCompact ? 28 : 0,
        duration,
        ease: PILL_GSAP_EASE,
        overwrite: 'auto',
      });

      if (inner) {
        gsap.to(inner, {
          paddingLeft: pad,
          paddingRight: padRight,
          duration,
          ease: PILL_GSAP_EASE,
          overwrite: 'auto',
        });
      }
    };

    const immediate = !pillTweenReadyRef.current;
    pillTweenReadyRef.current = true;
    tweenPill(scrolled, immediate);

    const onResize = () => tweenPill(scrolledRef.current, true);
    window.addEventListener('resize', onResize, { passive: true });
    // Do NOT clear props when `scrolled` flips — that would kill the in-flight
    // tween. Unmount cleanup is separate, below.
    return () => window.removeEventListener('resize', onResize);
  }, [scrolled]);

  useEffect(
    () => () => {
      const bar = barAndPanelRef.current;
      const inner = barInnerRef.current;
      if (bar) {
        gsap.killTweensOf(bar);
        gsap.set(bar, {
          clearProps:
            'maxWidth,marginTop,marginLeft,marginRight,marginBottom,borderRadius',
        });
      }
      if (inner) {
        gsap.killTweensOf(inner);
        gsap.set(inner, { clearProps: 'paddingLeft,paddingRight' });
      }
    },
    [],
  );

  const panelOpen = openKey !== null;

  return (
    <>
      <div
        className={`${styles.menuScrim} ${themeStyles[theme]}`}
        data-open={String(panelOpen)}
        aria-hidden="true"
        inert={!panelOpen}
      />
      <div
        ref={wrapperRef}
        className={`${styles.wrapper} ${themeStyles[theme]}`}
        data-hidden={String(isCollapsable && !GlobalUI.headerVisible)}
      >
        <div className={styles.bannerCollapse} data-scrolled={String(scrolled)}>
          <MarketingBanner />
        </div>
        <div onMouseEnter={clearCloseTimer} onMouseLeave={scheduleClose}>
          <div
            ref={barAndPanelRef}
            className={styles.barAndPanel}
            data-open={String(panelOpen)}
            data-scrolled={String(scrolled)}
            data-frosted={String(barFrosted)}
            data-campaign-bar={String(isOpaqueLightBar)}
          >
            <header className={styles.bar} data-panel-open={String(panelOpen)}>
              <div ref={barInnerRef} className={styles.barInner}>
                <div className={styles.left} data-scrolled={String(scrolled)}>
                  {/* Desktop: the ☰ opens the "More" panel and morphs to an X
                      while open. Click-only (tabs keep hover); registered in
                      triggerRefs so Escape returns focus. */}
                  <span className={styles.desktopOnly}>
                    <button
                      type="button"
                      className={styles.iconButton}
                      id={`mega-trigger-${MORE_MENU_KEY}`}
                      ref={(el) => registerTriggerRef(MORE_MENU_KEY, el)}
                      aria-label={
                        openKey === MORE_MENU_KEY
                          ? t('header.closeMenu')
                          : t('header.openMenu')
                      }
                      aria-expanded={openKey === MORE_MENU_KEY}
                      aria-controls={`mega-panel-${MORE_MENU_KEY}`}
                      onClick={() => handleToggleMenu(MORE_MENU_KEY)}
                    >
                      <HamburgerMorphIcon open={openKey === MORE_MENU_KEY} />
                    </button>
                  </span>
                  <span className={styles.desktopOnly}>
                    <nav aria-label={t('header.primaryNav')}>
                      <ul
                        className={styles.navList}
                        data-scrolled={String(scrolled)}
                      >
                        {visibleNavItems.map((item) => (
                          <li
                            key={item.key}
                            className={styles.navListItem}
                            onMouseEnter={() => {
                              clearCloseTimer();
                              if (item.kind === 'menu') {
                                hoverOpenedKeyRef.current = item.key;
                                setOpenKey(item.key);
                              } else {
                                hoverOpenedKeyRef.current = null;
                                setOpenKey(null);
                              }
                            }}
                          >
                            <NavTrigger
                              item={item}
                              active={openKey === item.key}
                              label={t(item.labelKey)}
                              onToggle={handleToggleMenu}
                              onArrowDown={openPanelAndFocusFirst}
                              registerRef={registerTriggerRef}
                            />
                          </li>
                        ))}
                      </ul>
                    </nav>
                  </span>
                  {/* Mobile: the compact logo home-link stays in .left so the
                      mobile bar is unchanged (.center is display:none <900px). */}
                  <span className={styles.mobileOnly}>
                    <CustomLink
                      href={{ pathname: `/${regionSlug}/` }}
                      aria-label={t('header.goHome')}
                      {...headerLinkAttribution({ label: 'logo' })}
                    >
                      <UHLogo
                        fill="currentColor"
                        className={styles.compactLogo}
                      />
                    </CustomLink>
                  </span>
                </div>

                <div className={styles.center}>
                  {/* Centred logo (desktop). The mark slides in as the letters
                      drop, driven by `scrolled`, and reverses from wherever
                      the transform currently is. */}
                  <CustomLink
                    href={{ pathname: `/${regionSlug}/` }}
                    aria-label={t('header.goHome')}
                    {...headerLinkAttribution({ label: 'logo' })}
                  >
                    <HeaderLogo scrolled={scrolled} />
                  </CustomLink>
                </div>

                <div className={styles.right} data-scrolled={String(scrolled)}>
                  {/* Mobile CTA(s) first so the cluster reads Buy now → flag →
                      cart → ☰ (prod parity). They are display:none ≥900px, so
                      desktop keeps flag → cart → CTA. DOM order matches visual
                      order at both breakpoints (WCAG 1.3.2). */}
                  {headerCTA.bloodVision && (
                    <span className={styles.mobileOnly}>
                      {renderCta(headerCTA.bloodVision, 'white')}
                    </span>
                  )}
                  <span className={styles.mobileOnly}>
                    {renderCta(headerCTA.mobile)}
                  </span>
                  <RegionSelector theme={theme} compactOnMobile />
                  <CartIcon iconTheme={theme} />
                  <span className={styles.desktopOnly}>
                    {renderCta(headerCTA.desktop)}
                  </span>
                  <span className={styles.mobileOnly}>
                    <button
                      ref={hamburgerRef}
                      type="button"
                      className={styles.iconButton}
                      /* Stable name + aria-expanded, rather than flipping to
                         "Close menu" — that duplicated the drawer's own close
                         button in the mobile a11y tree
                         (HEADER_V2_MIGRATION.md §2 M-1). */
                      aria-label={t('header.navigationMenu')}
                      aria-expanded={drawerOpen}
                      onClick={() => setDrawerOpen((o) => !o)}
                    >
                      <HamburgerMorphIcon open={drawerOpen} />
                    </button>
                  </span>
                </div>
              </div>
            </header>
          </div>
          <MegaMenu
            openKey={openKey}
            items={visibleNavItems}
            moreSections={MORE_MENU_SECTIONS}
            scrolled={scrolled}
            onSwitchTab={setOpenKey}
          />
        </div>

        <MobileDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          items={visibleNavItems}
          moreSections={MORE_MENU_SECTIONS}
          theme={theme}
          // The same CTA(s) the bar's mobile cluster renders, so the drawer's
          // top row is identical and nothing shifts or disappears on open.
          topCta={
            <>
              {headerCTA.bloodVision &&
                renderCta(headerCTA.bloodVision, 'white')}
              {renderCta(headerCTA.mobile)}
            </>
          }
          triggerRef={hamburgerRef}
        />
      </div>
    </>
  );
};

const NavTrigger = ({
  item,
  active,
  label,
  onToggle,
  onArrowDown,
  registerRef,
}: {
  item: NavItem;
  active: boolean;
  label: string;
  onToggle: (key: string) => void;
  onArrowDown: (key: string) => void;
  registerRef: (key: string, el: HTMLButtonElement | null) => void;
}) => {
  if (item.kind === 'link') {
    return (
      <CustomLink
        href={{ pathname: item.href }}
        className={styles.navLink}
        {...headerLinkAttribution()}
      >
        <span className={styles.navLabel}>{label}</span>
      </CustomLink>
    );
  }
  return (
    <button
      type="button"
      className={styles.navButton}
      id={`mega-trigger-${item.key}`}
      ref={(el) => registerRef(item.key, el)}
      aria-expanded={active}
      aria-controls={`mega-panel-${item.key}`}
      data-buttontype="header"
      onClick={() => onToggle(item.key)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          onArrowDown(item.key);
        }
      }}
    >
      <span className={styles.navLabel} data-active={String(active)}>
        {label}
      </span>
    </button>
  );
};
